import React from 'react'
import { Network, Workflow, Users2, CalendarDays, BookMarked, Gauge, BarChart3, Landmark, Link as LinkIcon, Cpu, ShieldCheck, FileCheck, Lock, DollarSign } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import { useTranslation } from 'react-i18next'
import CustomerLogos from '@/components/common/CustomerLogos'

const ReferralCX: React.FC = () => {
  const navigationStore = useNavigationStore()
  const { t } = useTranslation()

  const features = [
    {
      title: t('referralcx.features.items.internalExternal.title'),
      body: t('referralcx.features.items.internalExternal.body'),
      Icon: Network,
    },
    {
      title: t('referralcx.features.items.aiWorkflows.title'),
      body: t('referralcx.features.items.aiWorkflows.body'),
      Icon: Cpu,
    },
    {
      title: t('referralcx.features.items.collaboration.title'),
      body: t('referralcx.features.items.collaboration.body'),
      Icon: Users2,
    },
    {
      title: t('referralcx.features.items.scheduling.title'),
      body: t('referralcx.features.items.scheduling.body'),
      Icon: CalendarDays,
    },
    {
      title: t('referralcx.features.items.guidelines.title'),
      body: t('referralcx.features.items.guidelines.body'),
      Icon: BookMarked,
    },
    {
      title: t('referralcx.features.items.analytics.title'),
      body: t('referralcx.features.items.analytics.body'),
      Icon: BarChart3,
    },
  ]

  const useCases = [
    {
      title: t('referralcx.useCases.items.primarySpecialty.title'),
      body: t('referralcx.useCases.items.primarySpecialty.body'),
      Icon: Landmark,
    },
    {
      title: t('referralcx.useCases.items.community.title'),
      body: t('referralcx.useCases.items.community.body'),
      Icon: LinkIcon,
    },
    {
      title: t('referralcx.useCases.items.publicHealth.title'),
      body: t('referralcx.useCases.items.publicHealth.body'),
      Icon: Gauge,
    },
    {
      title: t('referralcx.useCases.items.anyToAny.title'),
      body: t('referralcx.useCases.items.anyToAny.body'),
      Icon: Workflow,
    },
  ]

  const faqs = [
    {
      q: t('referralcx.faq.items.integration.question'),
      a: t('referralcx.faq.items.integration.answer'),
      Icon: LinkIcon,
    },
    {
      q: t('referralcx.faq.items.security.question'),
      a: t('referralcx.faq.items.security.answer'),
      Icon: ShieldCheck,
    },
    {
      q: t('referralcx.faq.items.implementation.question'),
      a: t('referralcx.faq.items.implementation.answer'),
      Icon: FileCheck,
    },
    {
      q: t('referralcx.faq.items.pricing.question'),
      a: t('referralcx.faq.items.pricing.answer'),
      Icon: DollarSign,
    },
  ]

  return (
    <div className="min-h-screen w-full text-white">
      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur border-t border-slate-200 p-3 sm:hidden">
        <div className="flex gap-3">
          <button 
            onClick={navigationStore.openDemoModal}
            className="flex-1 text-center bg-[#395091] text-white font-semibold px-4 py-3 rounded-lg shadow-md hover:bg-[#2d3f7a] transition"
          >
            {t('common.bookDemo')}
          </button>
          <a href="#case-study" className="flex-1 text-center bg-white text-[#395091] font-semibold px-4 py-3 rounded-lg border border-[#395091]/30 hover:bg-gray-50 transition">Case Study</a>
        </div>
      </div>

      {/* HERO */}
      <header
        className="w-full"
        style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          {/* Product Wordmark */}
          <div className="mb-8 sm:mb-10">
            <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
              <span >referral</span>
              <span className="font-bold text-[#7AC943]">CX</span>
              <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">™</sup>
            </h2>
          </div>

          {/* Headline & Subcopy */}
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">{t('referralcx.hero.title')}</h1>
            <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
              {t('referralcx.hero.subtitle')}
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={navigationStore.openDemoModal}
                className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
              >
                {t('common.bookDemo')}
              </button>
              <a
                href="#case-study"
                className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
              >
                {t('common.downloadCaseStudy')}
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* BODY */}
      <main className="bg-white text-slate-800">
        {/* Key Features */}
        <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">{t('referralcx.features.title')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map(({ title, body, Icon }, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition bg-white/70 backdrop-blur-[1px]"
              >
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#F0F5FF]">
                    <Icon className="w-5 h-5 text-[#395091]" aria-hidden="true" />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">{title}</h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section
          id="use-cases"
          className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#EDF7FF] to-[#F9FBFF] rounded-3xl"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">{t('referralcx.useCases.title')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {useCases.map(({ title, body, Icon }, i) => (
              <div key={i} className="rounded-2xl bg-gradient-to-tr from-[#F0F5FF] to-white border border-slate-200 p-5 sm:p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                    <Icon className="w-5 h-5 text-[#395091]" aria-hidden="true" />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">{title}</h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Client Story + Why */}
        <section id="case-study" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 scroll-mt-24 md:scroll-mt-28">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-start">
            {/* Client Story */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 sm:mb-4">{t('referralcx.caseStudy.title')}</h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                {t('referralcx.caseStudy.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <a
                  href="/use-case-finder?Org=CHOC"
                  className="inline-block rounded-lg bg-[#395091] text-white px-5 py-3 font-semibold hover:opacity-90 text-center"
                >
                  {t('common.viewCaseStudy')}
                </a>
                <a href="/use-case-finder" className="inline-block text-[#395091] font-semibold hover:underline text-center">
                  {t('referralcx.caseStudy.seeMoreStories')}
                </a>
              </div>
              {/* Client logos row */}
              <CustomerLogos logoUids={["dfci", "stlukes", "nhs"]} />
            </div>

            {/* Why ReferralCX */}
            <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-gradient-to-br from-[#F9FAFB] to-white">
              <h4 className="font-semibold text-slate-900 mb-3 text-lg">{t('referralcx.whyReferralcx.title')}</h4>
              <p className="text-slate-700 text-base leading-relaxed mb-4">
                {t('referralcx.whyReferralcx.description')}
              </p>
              <ul className="space-y-3 text-base">
                <li>
                  <details className="group">
                    <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                      <Workflow className="w-5 h-5 text-[#395091]" /> {t('referralcx.whyReferralcx.workflowManagement.title')}
                    </summary>
                    <div className="mt-2 text-slate-600">
                      <ul className="list-disc ml-5 space-y-1">
                        {(t('referralcx.whyReferralcx.workflowManagement.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </details>
                </li>
                <li>
                  <details className="group">
                    <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                      <BookMarked className="w-5 h-5 text-[#395091]" /> {t('referralcx.whyReferralcx.referralProcesses.title')}
                    </summary>
                    <div className="mt-2 text-slate-600">
                      <ul className="list-disc ml-5 space-y-1">
                        {(t('referralcx.whyReferralcx.referralProcesses.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </details>
                </li>
                <li>
                  <details className="group">
                    <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                      <Network className="w-5 h-5 text-[#395091]" /> {t('referralcx.whyReferralcx.integration.title')}
                    </summary>
                    <div className="mt-2 text-slate-600">
                      <ul className="list-disc ml-5 space-y-1">
                        {(t('referralcx.whyReferralcx.integration.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </details>
                </li>
                <li>
                  <details className="group">
                    <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                      <CalendarDays className="w-5 h-5 text-[#395091]" /> {t('referralcx.whyReferralcx.scheduling.title')}
                    </summary>
                    <div className="mt-2 text-slate-600">
                      <ul className="list-disc ml-5 space-y-1">
                        {(t('referralcx.whyReferralcx.scheduling.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </details>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Trust & Compliance */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{t('referralcx.trust.title')}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('referralcx.trust.hipaa')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FileCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('referralcx.trust.soc2')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Lock className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('referralcx.trust.sso')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('referralcx.trust.gdpr')}</span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{t('referralcx.faq.title')}</h3>
          <ul className="space-y-4">
            {faqs.map(({ q, a, Icon }, i) => (
              <li key={i} className="border border-slate-200 rounded-lg p-4">
                <details className="group">
                  <summary className="flex items-center gap-2 font-medium cursor-pointer text-slate-900">
                    <Icon className="w-5 h-5 text-[#395091]" /> {q}
                  </summary>
                  <p className="mt-2 text-slate-600 leading-relaxed">{a}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        {/* Final CTA */}
        <section id="contact" className="relative mt-6 sm:mt-8">
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }} />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">{t('referralcx.cta.title')}</h3>
                <p className="opacity-90 text-base sm:text-lg">{t('referralcx.cta.subtitle')}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button
                  onClick={navigationStore.openDemoModal}
                  className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
                >
                  {t('common.bookDemo')}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Footer note */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
          {t('referralcx.footer')}
        </section>
      </main>
    </div>
  )
}

export default ReferralCX


